import { type FC, useId, useState } from "react";

import { Alert, Group, SegmentedControl, Skeleton, Stack, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { Colors, Spacing } from "@snapchat/snap-design-system";
import { InformationIcon, Sizes } from "@snapchat/snap-design-system-icons";
import {
    Bar,
    type BarShapeProps,
    CartesianGrid,
    BarChart as RechartsBarChart,
    Rectangle,
    Tooltip,
    XAxis,
    type XAxisTickContentProps,
    YAxis,
} from "recharts";

import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import {
    AwardBarKind,
    AwardHistoryRange,
    DEFAULT_AWARD_HISTORY_RANGE,
    type TopPerformerLensRow,
    isAwardHistoryRange,
} from "../../models";
import {
    type AwardChartDatum,
    buildAwardBars,
    formatAwardMonthLabel,
    formatAwardMonthTick,
    formatWholeUsd,
    isAwardChartDatum,
    toAwardChartData,
} from "../../utils";

import {
    cardContent,
    chart,
    chartContainer,
    footnote,
    header,
    legend,
    swatch,
    swatchAwarded,
    swatchInProgress,
    tooltipPortal,
    total,
    totalLabel,
} from "./AwardHistoryCard.module.css";
import { AwardHistoryTooltip } from "./AwardHistoryTooltip";
import {
    HistoryError,
    HistoryFootnote,
    HistorySubtitle,
    HistoryTitle,
    LegendAwarded,
    LegendInProgress,
    NoAward,
    Pending,
    RangeLast12Months,
    RangeLast6Months,
    RangeLifetime,
    TotalAllTime,
    TotalSelectedRange,
} from "./messages.generated";

const CHART_HEIGHT = 240;
const Y_AXIS_WIDTH = 80;
const MIN_MONTH_WIDTH = Spacing["3XL"] + Spacing.LG;
const X_AXIS_HEIGHT = Spacing["2XL"];
const TOTAL_SKELETON_HEIGHT = Spacing.LG;
const TOTAL_SKELETON_WIDTH = Spacing["3XL"] * 2;
const RANGE_OPTIONS = [
    { value: String(AwardHistoryRange.Lifetime), label: <RangeLifetime /> },
    { value: String(AwardHistoryRange.Last6Months), label: <RangeLast6Months /> },
    { value: String(AwardHistoryRange.Last12Months), label: <RangeLast12Months /> },
];
const BAR_GAP_BY_RANGE: Record<AwardHistoryRange, string> = {
    [AwardHistoryRange.Lifetime]: "15%",
    [AwardHistoryRange.Last6Months]: "30%",
    [AwardHistoryRange.Last12Months]: "15%",
};

type AwardTickProps = Pick<XAxisTickContentProps, "x" | "y" | "index">;

const AwardTick: FC<AwardTickProps & { data: AwardChartDatum[]; showYear: boolean }> = ({
    x,
    y,
    index,
    data,
    showYear,
}) => {
    const datum = data[index];
    if (!datum) return null;
    return (
        <g transform={`translate(${x},${y})`}>
            <text textAnchor="middle" dy={Spacing.SM} fill={Colors.Black.V50}>
                {showYear ? formatAwardMonthLabel(datum.monthStart) : formatAwardMonthTick(datum.monthStart)}
            </text>
            <text textAnchor="middle" dy={Spacing.XL} fill={Colors.Black.V50}>
                {datum.kind === AwardBarKind.InProgress ? <Pending /> : formatWholeUsd(datum.amountMilliUsd)}
            </text>
        </g>
    );
};

const barFill = (kind: AwardBarKind, stripeId: string): string =>
    ({
        [AwardBarKind.Awarded]: Colors.Black.V100,
        [AwardBarKind.InProgress]: `url(#${stripeId})`,
        [AwardBarKind.Empty]: Colors.Gray.V150,
    })[kind];

interface AwardHistoryCardProps {
    lenses: readonly TopPerformerLensRow[];
    loading: boolean;
    hasError: boolean;
}

export const AwardHistoryCard: FC<AwardHistoryCardProps> = ({ lenses, loading, hasError }) => {
    const [range, setRange] = useState(DEFAULT_AWARD_HISTORY_RANGE);
    const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);
    const { data, max, ticks } = toAwardChartData(buildAwardBars(lenses, new Date(), range));
    const totalForRangeMilliUsd = data.reduce((sum, bar) => sum + bar.amountMilliUsd, 0);
    const { ref, width } = useElementSize();
    const titleId = useId();
    const chartWidth = Math.max(width, Y_AXIS_WIDTH + data.length * MIN_MONTH_WIDTH);
    // useId contains colons, which are not valid in an SVG url(#id) reference.
    const stripeId = useId().replaceAll(":", "");

    return (
        <Stack gap="md" className={cardContent}>
            <div className={header}>
                <div>
                    <h2 id={titleId} className="heading-h400">
                        <HistoryTitle />
                    </h2>
                    <Text className="paragraph-p300">
                        <HistorySubtitle />
                    </Text>
                </div>
                <Group gap="md">
                    <div className={total}>
                        <div className={`subheading-sh500 ${totalLabel}`}>
                            {range === AwardHistoryRange.Lifetime ? <TotalAllTime /> : <TotalSelectedRange />}
                        </div>
                        <div className="heading-h400" data-testid="award-history-total">
                            {loading ? (
                                <Skeleton h={TOTAL_SKELETON_HEIGHT} w={TOTAL_SKELETON_WIDTH} ml="auto" />
                            ) : hasError ? (
                                <NoAward />
                            ) : (
                                formatMilliUsd(totalForRangeMilliUsd)
                            )}
                        </div>
                    </div>
                    <SegmentedControl
                        radius="xl"
                        size="xs"
                        value={String(range)}
                        onChange={(value) => {
                            const next = Number(value);
                            if (isAwardHistoryRange(next)) setRange(next);
                        }}
                        data={RANGE_OPTIONS}
                    />
                </Group>
            </div>
            <Group gap="md" justify="flex-end" className={`paragraph-p300 ${legend}`}>
                <span>
                    <span className={`${swatch} ${swatchAwarded}`} aria-hidden="true" />
                    <LegendAwarded />
                </span>
                <span>
                    <span className={`${swatch} ${swatchInProgress}`} aria-hidden="true" />
                    <LegendInProgress />
                </span>
            </Group>
            <div className={chartContainer}>
                <div
                    ref={ref}
                    className={chart}
                    role="region"
                    aria-labelledby={titleId}
                    tabIndex={!hasError && !loading && width > 0 && chartWidth > width ? 0 : undefined}
                >
                    {hasError ? (
                        <Alert color="red" variant="light">
                            <HistoryError />
                        </Alert>
                    ) : loading || width === 0 ? (
                        <Skeleton h={CHART_HEIGHT} w="100%" data-testid="award-history-loading" />
                    ) : (
                        <RechartsBarChart
                            width={chartWidth}
                            height={CHART_HEIGHT}
                            data={data}
                            barCategoryGap={BAR_GAP_BY_RANGE[range]}
                        >
                            <defs>
                                <pattern
                                    id={stripeId}
                                    width={Spacing.XS}
                                    height={Spacing.XS}
                                    patternUnits="userSpaceOnUse"
                                    patternTransform="rotate(45)"
                                >
                                    <rect width={Spacing.XS} height={Spacing.XS} fill={Colors.Yellow.V50} />
                                    <rect width={Spacing["2XS"]} height={Spacing.XS} fill={Colors.Yellow.V200} />
                                </pattern>
                            </defs>
                            <CartesianGrid vertical={false} stroke={Colors.Gray.V100} />
                            <XAxis
                                dataKey="monthStart"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                height={X_AXIS_HEIGHT}
                                tick={(props: AwardTickProps) => (
                                    <AwardTick {...props} data={data} showYear={range === AwardHistoryRange.Lifetime} />
                                )}
                            />
                            <YAxis
                                width={Y_AXIS_WIDTH}
                                domain={[0, max]}
                                ticks={ticks}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: Colors.Black.V50 }}
                                tickFormatter={(value: number) => formatWholeUsd(value)}
                            />
                            <Tooltip
                                content={<AwardHistoryTooltip viewport={ref} />}
                                cursor={false}
                                portal={tooltipContainer}
                                isAnimationActive={false}
                            />
                            <Bar
                                dataKey="value"
                                minPointSize={Spacing["2XS"]}
                                radius={[Spacing["1XS"], Spacing["1XS"], 0, 0]}
                                isAnimationActive={false}
                                shape={(props: BarShapeProps) => {
                                    const datum: unknown = props.payload;
                                    return (
                                        <Rectangle
                                            {...props}
                                            fill={barFill(
                                                isAwardChartDatum(datum) ? datum.kind : AwardBarKind.Empty,
                                                stripeId,
                                            )}
                                        />
                                    );
                                }}
                            />
                        </RechartsBarChart>
                    )}
                </div>
                <div ref={setTooltipContainer} className={tooltipPortal} />
            </div>
            <div className={`paragraph-p300 ${footnote}`}>
                <InformationIcon size={Sizes.Small} aria-hidden="true" />
                <HistoryFootnote />
            </div>
        </Stack>
    );
};
